// src/pages/finance-portal/components/FinanceAccountFilter.js
//
// Global CRM Account filter for the Finance Portal.
// Visual style mirrors the Header chatbot selector (two-line desktop button +
// icon-only mobile button, MUI Menu dropdown). Rendered from Header.js,
// conditional on activePanel === PanelType.FINANCE.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Button, IconButton, Tooltip,
    Menu, MenuItem, ListItemIcon, Divider, Chip,
    Dialog, DialogTitle, DialogContent, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Skeleton,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AppsIcon from '@mui/icons-material/Apps';
import { useFinanceAccount } from '../../../contexts/FinanceAccountContext';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',
};

export default function FinanceAccountFilter({ headerColor = '#22c55e' }) {
    const { selectedAccount, setSelectedAccount, recentAccounts, clearAccount } = useFinanceAccount();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [allOpen, setAllOpen] = useState(false);

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: page + 1, per_page: 10 };
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/crm-accounts', { params });
            if (res.data?.success) {
                setAccounts(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (_) { /* silent */ }
        finally { setLoading(false); }
    }, [page, search]);

    useEffect(() => { if (allOpen) fetchAccounts(); }, [allOpen, fetchAccounts]);

    const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
    const handleMenuClose = () => setMenuAnchor(null);

    const handlePick = async (acc) => {
        const changed = selectedAccount?.id !== acc?.id;
        await setSelectedAccount(acc);
        handleMenuClose();
        setAllOpen(false);
        if (changed) {
            window.location.reload();
        }
    };

    const handleClear = () => {
        const changed = !!selectedAccount;
        clearAccount();
        handleMenuClose();
        if (changed) {
            window.location.reload();
        }
    };

    const handleOpenAll = () => {
        handleMenuClose();
        setAllOpen(true);
        setSearch('');
        setPage(0);
    };

    const tintBg = `${headerColor}14`;
    const tintBgHover = `${headerColor}1f`;
    const tintBorder = `${headerColor}26`;

    return (
        <Box sx={{ mr: { xs: 0, md: 1 } }}>
            {/* Mobile: icon-only */}
            <Tooltip title={selectedAccount ? selectedAccount.name : 'Select Account'}>
                <IconButton
                    onClick={handleMenuOpen}
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: tintBg,
                        border: `1px solid ${tintBorder}`,
                        '&:hover': { bgcolor: tintBgHover },
                    }}
                >
                    <BusinessIcon sx={{ fontSize: '1.1rem', color: headerColor }} />
                </IconButton>
            </Tooltip>

            {/* Desktop: full two-line button */}
            <Button
                onClick={handleMenuOpen}
                startIcon={<BusinessIcon sx={{ fontSize: '1.1rem', color: headerColor }} />}
                endIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    fontFamily,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    color: colors.textPrimary,
                    bgcolor: tintBg,
                    borderRadius: '10px',
                    px: 2,
                    py: 0.75,
                    minWidth: 200,
                    justifyContent: 'space-between',
                    border: `1px solid ${tintBorder}`,
                    '&:hover': { bgcolor: tintBgHover },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, ml: 0.5, minWidth: 0 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: headerColor, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                        Active Client
                    </Typography>
                    <Typography
                        noWrap
                        sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2, maxWidth: 160 }}
                    >
                        {selectedAccount ? selectedAccount.name : 'All Clients'}
                    </Typography>
                </Box>
            </Button>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        minWidth: { xs: 240, sm: 300 },
                        maxWidth: { xs: 300, sm: 'none' },
                        maxHeight: { xs: '60vh', sm: 420 },
                        mt: 1,
                        borderRadius: { xs: '10px', sm: '12px' },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '3px' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                    },
                }}
            >
                <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Filter Finance By Client
                    </Typography>
                </Box>

                <MenuItem
                    onClick={handleClear}
                    selected={!selectedAccount}
                    sx={{
                        py: { xs: 1, sm: 1.5 },
                        minHeight: 'auto',
                        '&.Mui-selected': { bgcolor: tintBg, '&:hover': { bgcolor: tintBgHover } },
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                        <BusinessIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: !selectedAccount ? headerColor : colors.textTertiary }} />
                    </ListItemIcon>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 500, color: colors.textPrimary }}>
                            All Clients
                        </Typography>
                        {!selectedAccount && (
                            <Chip
                                label="Active"
                                size="small"
                                sx={{ height: { xs: 16, sm: 18 }, mt: 0.5, fontSize: '0.6rem', bgcolor: `${headerColor}26`, color: headerColor }}
                            />
                        )}
                    </Box>
                </MenuItem>

                {recentAccounts.length > 0 && (
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                        <HistoryIcon sx={{ fontSize: 13, color: colors.textTertiary }} />
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Recently Used ({recentAccounts.length})
                        </Typography>
                    </Box>
                )}

                {recentAccounts.map(acc => {
                    const active = selectedAccount?.id === acc.id;
                    return (
                        <MenuItem
                            key={acc.id}
                            onClick={() => handlePick(acc)}
                            selected={active}
                            sx={{
                                py: { xs: 1, sm: 1.5 },
                                minHeight: 'auto',
                                '&.Mui-selected': { bgcolor: tintBg, '&:hover': { bgcolor: tintBgHover } },
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                                {active
                                    ? <CheckIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: headerColor }} />
                                    : <BusinessIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: colors.textTertiary }} />
                                }
                            </ListItemIcon>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    noWrap
                                    sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 500, color: colors.textPrimary }}
                                >
                                    {acc.name}
                                </Typography>
                                {acc.industry && (
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary }} noWrap>
                                        {acc.industry}
                                    </Typography>
                                )}
                            </Box>
                            {active && (
                                <Chip
                                    label="Active"
                                    size="small"
                                    sx={{ ml: 1, height: { xs: 16, sm: 18 }, fontSize: '0.6rem', bgcolor: `${headerColor}26`, color: headerColor }}
                                />
                            )}
                        </MenuItem>
                    );
                })}

                <Divider sx={{ my: { xs: 0.5, sm: 1 } }} />

                <MenuItem onClick={handleOpenAll} sx={{ py: { xs: 0.75, sm: 1.5 }, minHeight: 'auto' }}>
                    <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                        <AppsIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: headerColor }} />
                    </ListItemIcon>
                    <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600, color: headerColor }}>
                        Show All Clients
                    </Typography>
                </MenuItem>
            </Menu>

            {/* All accounts dialog */}
            <Dialog open={allOpen} onClose={() => setAllOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ color: headerColor }} />
                        Select Client
                    </Box>
                    <IconButton size="small" onClick={() => setAllOpen(false)} sx={{ color: colors.textTertiary }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search accounts..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: colors.textTertiary }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ mb: 2 }}
                    />

                    <TableContainer sx={{ maxHeight: 380 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {['Name', 'Industry', 'Status', ''].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', bgcolor: '#f8fafc' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [...Array(5)].map((_, i) => (
                                        <TableRow key={i}><TableCell colSpan={4}><Skeleton height={32} /></TableCell></TableRow>
                                    ))
                                ) : accounts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ fontFamily, color: colors.textTertiary, fontSize: '0.85rem' }}>
                                                {search ? 'No accounts match your search' : 'No CRM accounts available'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : accounts.map(acc => {
                                    const active = selectedAccount?.id === acc.id;
                                    return (
                                        <TableRow
                                            key={acc.id}
                                            hover
                                            onClick={() => handlePick(acc)}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: tintBg } }}
                                        >
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.textPrimary }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    {active && <CheckIcon sx={{ fontSize: 14, color: headerColor }} />}
                                                    {acc.name}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: colors.textSecondary }}>{acc.industry || '—'}</TableCell>
                                            <TableCell>
                                                {acc.status && (
                                                    <Chip
                                                        label={acc.status}
                                                        size="small"
                                                        sx={{ fontFamily, fontSize: '0.65rem', height: 20, bgcolor: '#f1f5f9', color: '#475569' }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={(e) => { e.stopPropagation(); handlePick(acc); }}
                                                    sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: headerColor, color: headerColor, minWidth: 'auto', px: 1.5, '&:hover': { borderColor: headerColor, bgcolor: tintBg } }}
                                                >
                                                    Select
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {total > 10 && (
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={10}
                            rowsPerPageOptions={[10]}
                            sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
